import { useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useForm } from "react-hook-form";

import type { MfaPassword } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { queryKeys } from "~/api/queryKeys";
import FormLabel from "~/components/login/formLabel/FormLabel";
import FormBaseErrors from "~/components/ui/formBaseErrors/FormBaseErrors";
import Input from "~/components/ui/input/Input";
import Modal from "~/components/ui/modal/Modal";
import { useAuth } from "~/contexts/AuthProvider";
import labels from "~/helpers/labels";
import { tryCatchParsed } from "~/helpers/tryCatch";
import { useFormErrors } from "~/hooks/useFormErrors";

import styles from "./TwoFactor.module.scss";

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const DisableMfaModal = ({ isOpen, onClose }: Props) => {
  const queryClient = useQueryClient();
  const { token, refetchUser } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
  } = useForm<MfaPassword>({ defaultValues: { current_password: "" } });
  const { baseErrors, applyServerErrors, clearBaseErrors } =
    useFormErrors(setError);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: MfaPassword) => apiClient.mfa.deleteMfa(data),
  });

  const submit = handleSubmit(async (data) => {
    clearBaseErrors();
    const result = await tryCatchParsed(mutateAsync(data));

    if (result.error) {
      applyServerErrors(result.error);

      return;
    }

    enqueueSnackbar("Two-factor authentication disabled", {
      variant: "success",
    });
    await refetchUser();
    queryClient.invalidateQueries({ queryKey: queryKeys.mfaStatus(token) });
    handleClose();
  });

  const handleClose = () => {
    reset({ current_password: "" });
    clearBaseErrors();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModal={isPending ? () => {} : handleClose}
      modalLabel={<h3>Disable Two-Factor Authentication</h3>}
      buttonLabel="Disable"
      action={submit}
      actionButtonColor="danger"
      isLoading={isPending}
    >
      <p>
        Are you sure you want to disable two-factor authentication? Your account
        will be less secure.
      </p>
      <FormBaseErrors errors={baseErrors} />
      {/* Re-auth: requiring the current password prevents an attacker with a
          stolen session token from silently disabling MFA. */}
      <form onSubmit={submit}>
        <div className={styles.inputWrapper}>
          <FormLabel label="Current password" />
          <Input
            type="password"
            autoComplete="current-password"
            disabled={isPending}
            errormessage={errors.current_password?.message ?? ""}
            {...register("current_password", {
              required: labels.THIS_FIELD_CANNOT_BE_EMPTY,
            })}
          />
        </div>
      </form>
    </Modal>
  );
};

export default DisableMfaModal;
